import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useNavigationType } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, Icon, NativeSelect, SearchField } from "../../ui";
import UserCard from "../../ui/Users/UserCard";
import Pagination from "../../ui/Data/Pagination";
import UsersLoading from "../../ui/Users/UsersLoading";
import UsersError from "../../ui/Users/UsersError";
import { StatsGrid } from "../../ui/Data/StatCard";
import { useUsersData } from "../../../hooks/useUsers";
import { useExportUsers } from "../../../hooks/useExportUsers";
import { useUrlStates, usePostHog } from "@finitless/shared/hooks";
import { toast } from "sonner";
import {
  ENABLED_FILTER_OPTIONS,
  ITEMS_PER_PAGE,
  MAX_VISIBLE_PAGES,
} from "../../../constants/userConstants";
import { PageHeader } from "@finitless/shared/components";
import CreateUserModal from "../../ui/Modals/CreateUserModal";

const AdminsPage = () => {
  const { t } = useTranslation("customers");
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();

  const [urlStates, updateUrlState] = useUrlStates({
    enabledFilter: "all",
    searchTerm: "",
    currentPage: "1",
  });

  const [searchInput, setSearchInput] = useState(urlStates.searchTerm || "");
  const [enabledFilter, setEnabledFilter] = useState(
    urlStates.enabledFilter || "all",
  );
  const [searchTerm, setSearchTerm] = useState(urlStates.searchTerm || "");
  const [currentPage, setCurrentPage] = useState(
    parseInt(urlStates.currentPage) || 1,
  );
  const [isCreateUserModalOpen, setIsCreateUserModalOpen] = useState(false);

  // Debounce search input
  useEffect(() => {
    if (searchInput === searchTerm) return;
    const timer = setTimeout(() => {
      setSearchTerm(searchInput);
      updateUrlState("searchTerm", searchInput);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchInput, searchTerm, updateUrlState]);

  // Browser history owns restored queries; typing still commits after 500 ms.
  // Own REPLACE updates must not erase an uncommitted search draft.
  useEffect(() => {
    if (navigationType !== "POP") return;
    const params = new URLSearchParams(location.search);
    const restoredSearch = params.get("searchTerm") || "";
    setSearchInput(restoredSearch);
    setSearchTerm(restoredSearch);
    setEnabledFilter(params.get("enabledFilter") || "all");
    setCurrentPage(parseInt(params.get("currentPage")) || 1);
  }, [location.search, navigationType]);

  // Get admin data
  const { stats, users, pagination, isLoading, error } = useUsersData(
    "admins",
    enabledFilter,
    searchTerm,
    currentPage,
  );

  // Export users hook
  const exportUsersMutation = useExportUsers();
  const { trackFeatureAdoption } = usePostHog();

  const paginationData = pagination;

  // Calculate totalPages manually if not provided by API
  const calculatedTotalPages = paginationData?.total
    ? Math.ceil(paginationData.total / ITEMS_PER_PAGE)
    : 1;

  const totalPages = paginationData?.totalPages || calculatedTotalPages;

  const handleEnabledFilterChange = (newEnabledFilter) => {
    setEnabledFilter(newEnabledFilter);
    setCurrentPage(1);
    updateUrlState("enabledFilter", newEnabledFilter);
    updateUrlState("currentPage", "1");
  };

  const handleSearchChange = (term) => {
    setSearchInput(term);
    setCurrentPage(1);
    updateUrlState("currentPage", "1");
  };

  const handleClearSearch = () => {
    setSearchInput("");
    setSearchTerm("");
    setCurrentPage(1);
    updateUrlState("searchTerm", "");
    updateUrlState("currentPage", "1");
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    updateUrlState("currentPage", page.toString());
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleViewDetails = (user) => {
    navigate(`/settings/admins/${user.id}`, { state: { user } });
  };

  const handleExportUsers = async () => {
    try {
      await exportUsersMutation.mutateAsync({
        userType: "admins",
        enabledFilter,
        searchTerm,
      });

      trackFeatureAdoption.dataExported({
        export_type: "users",
        format: "csv",
        user_type: "admins",
        enabled_filter: enabledFilter,
      });

      toast.success(t("toasts.exportSuccessTitle"), {
        description: t("toasts.exportSuccessDescription"),
        duration: 5000,
      });
    } catch {
      toast.error(t("toasts.exportFailedTitle"), {
        description: t("toasts.exportFailedDescription"),
        duration: 5000,
      });
    }
  };

  const metricCards = [
    {
      id: "total-admins",
      title: t("adminsPage.stats.totalAdmins"),
      value: stats?.totaladmins || stats?.totalAdmins || 0,
      icon: "Shield",
      iconColor: "text-primary",
      iconBg: "bg-accent",
      valueColor: "text-card-foreground",
      className: "border-border bg-card shadow-sm",
      gradient: false,
    },
  ];

  return (
    <div className="min-w-0 space-y-6" data-ordering-surface="admins">
      {/* Header */}
      <div className="flex min-w-0 flex-col justify-between gap-3 sm:flex-row sm:items-start">
        <PageHeader
          title={t("adminsPage.header.title")}
          subtitle={t("adminsPage.subtitle")}
        />
        <div className="flex max-w-full flex-wrap gap-2 sm:gap-3">
          <Button
            type="button"
            onClick={() => setIsCreateUserModalOpen(true)}
            emphasis="solid"
            intent="brand"
            size="touch"
            className="min-w-11 px-3 sm:px-4"
          >
            <Icon name="Plus" className="h-4 w-4" />
            <span className="hidden sm:inline">
              {t("adminsPage.actions.createAdmin")}
            </span>
            <span className="sm:hidden">{t("adminsPage.actions.create")}</span>
          </Button>
          <Button
            type="button"
            onClick={handleExportUsers}
            disabled={exportUsersMutation.isPending}
            aria-busy={exportUsersMutation.isPending || undefined}
            emphasis="outline"
            intent="neutral"
            size="touch"
            className="min-w-11 px-3 sm:px-4"
          >
            <Icon name="Download" className="h-4 w-4" />
            <span className="hidden sm:inline">
              {exportUsersMutation.isPending
                ? t("actions.exporting")
                : t("adminsPage.actions.exportAdmins")}
            </span>
            <span className="sm:hidden">
              {exportUsersMutation.isPending
                ? "..."
                : t("adminsPage.actions.export")}
            </span>
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <StatsGrid
        stats={metricCards}
        columns={metricCards.length}
        className="mb-4 sm:mb-6"
      />

      {/* Filters and Search */}
      <div className="border-border bg-card mb-6 rounded-lg border p-4 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-1 flex-col gap-4 sm:flex-row">
            {/* Search */}
            <div className="min-w-0 flex-1 [&_button]:h-11 [&_button]:w-11">
              <SearchField
                value={searchInput}
                onValueChange={handleSearchChange}
                onClear={handleClearSearch}
                clearLabel={t("actions.clearSearch", {
                  ns: "agentTranslations",
                })}
                aria-label={t("adminsPage.search.placeholder")}
                placeholder={t("adminsPage.search.placeholder")}
                size="lg"
              />
            </div>

            {/* Enabled Filter */}
            <div className="flex-shrink-0">
              <NativeSelect
                value={enabledFilter}
                onChange={(e) => handleEnabledFilterChange(e.target.value)}
                aria-label={t("adminsPage.filters.allAdmins")}
                className="h-11 w-full"
              >
                {ENABLED_FILTER_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {{
                      all: t("adminsPage.filters.allAdmins"),
                      enabled: t("filters.active"),
                      disabled: t("filters.inactive"),
                    }[option.value] || option.label}
                  </option>
                ))}
              </NativeSelect>
            </div>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {isLoading ? (
        <UsersLoading />
      ) : error ? (
        <UsersError error={error} />
      ) : users.length > 0 ? (
        <>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {users.map((user) => (
              <UserCard
                key={user.id}
                user={user}
                onViewDetails={handleViewDetails}
              />
            ))}
          </div>

          {/* Pagination */}
          <div className="mt-8">
            <div className="border-border bg-card rounded-lg border p-4 shadow-sm">
              <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
                <div className="text-muted-foreground text-sm">
                  {t("pagination.showing", {
                    from: (currentPage - 1) * ITEMS_PER_PAGE + 1,
                    to: Math.min(
                      currentPage * ITEMS_PER_PAGE,
                      paginationData?.total || 0,
                    ),
                    total: paginationData?.total || 0,
                  })}
                </div>

                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  maxVisiblePages={MAX_VISIBLE_PAGES}
                  disabled={isLoading}
                  className="mt-4 sm:mt-0"
                />
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="py-12 text-center">
          <Icon
            name="Shield"
            className="text-muted-foreground mx-auto mb-4 h-12 w-12"
          />
          <h3 className="text-foreground mt-2 text-sm font-medium">
            {t("adminsPage.empty.title")}
          </h3>
          <p className="text-muted-foreground mt-1 text-sm">
            {searchTerm
              ? t("adminsPage.empty.subtitleSearch")
              : t("adminsPage.empty.subtitleDefault")}
          </p>
        </div>
      )}

      {/* Create Admin Modal */}
      <CreateUserModal
        isOpen={isCreateUserModalOpen}
        onClose={() => setIsCreateUserModalOpen(false)}
      />
    </div>
  );
};

export default AdminsPage;
